// src/services/orderItemKinds.ts
// -----------------------------
// The canonical taxonomy of ORDERABLE item kinds for the Unleashed Order Builder,
// and the complete set of (kind, key) pairs that MUST have a product mapping in
// `unleashed_product_map` (db-66).
//
// WHY THIS EXISTS: the design principle (John, 12 July 2026) is that it must be
// IMPOSSIBLE for an approved item to reach the Order Builder without a product
// code. The device/fee kinds are Postgres enums, so their value set is fixed and
// knowable — this module derives the required mapping keys straight from the
// generated enum arrays, and `orderItemKinds.test.ts` asserts the derivation is
// exhaustive. So if anyone adds a new device_type/fee_type enum value in a future
// migration WITHOUT also mapping it, the CI test goes red.
//
// UNIFORMS are NOT here: they are keyed by SUPPLIER (admin-managed, dynamic), so a
// uniform's product code lives as a gated field on the supplier itself (the
// supplier form requires it), not as a fixed-enum row in this map. Shoes have two
// fixed kinds (jogger / leather) that the request captures as separate qty/cost
// pairs.

import { Constants } from "../types/database.generated";

export type ItemKind = "device" | "fee" | "shoe";

export interface ProductMapKey {
  kind: ItemKind;
  key: string;
}

// The fixed-enum value sets, sourced from the generated Supabase enum arrays so
// they can never drift from the database.
export const DEVICE_TYPES: readonly string[] = Constants.public.Enums.device_type_enum;
export const FEE_TYPES: readonly string[] = Constants.public.Enums.fee_type_enum;
// Shoes aren't an enum — the request stores jogger/leather as separate columns.
export const SHOE_KINDS = ["jogger", "leather"] as const;

// The complete list of (kind, key) pairs that require a product mapping. The
// Order Builder and the Product Mapping admin screen both use this as the source
// of "what must be mapped"; the DB seed (db-66) mirrors it (FIX_IT where a real
// code isn't known yet).
export const REQUIRED_PRODUCT_MAP_KEYS: ProductMapKey[] = [
  ...DEVICE_TYPES.map((key) => ({ kind: "device" as const, key })),
  ...FEE_TYPES.map((key) => ({ kind: "fee" as const, key })),
  ...SHOE_KINDS.map((key) => ({ kind: "shoe" as const, key })),
];

// The sentinel product code used where a real Unleashed code isn't known yet.
// A mapping pointing at FIX_IT is "not really mapped" — the admin screen flags
// it and the Order Builder must refuse to push it.
export const FIX_IT_CODE = "FIX_IT";
